// src/pages/ReferencePage.js - Reference guide page component
import React from 'react';
import styled from 'styled-components';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin: var(--spacing-xl) 0 var(--spacing-lg);
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-xl);
`;

const DocumentDownload = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  background-color: var(--background-light);
  color: var(--text-color);
  text-decoration: none;
  margin-bottom: var(--spacing-md);
  transition: background-color var(--transition-speed) ease;
  
  &:hover {
    background-color: var(--background-dark);
    text-decoration: none;
  }
  
  .icon {
    font-size: 1.5rem;
  }
  
  .details {
    flex: 1;
  }
  
  .title {
    font-weight: 600;
    margin-bottom: 4px;
  }
  
  .description {
    font-size: 0.875rem;
    color: var(--text-light);
  }
`;

const ExternalResource = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  background-color: var(--background-light);
  color: var(--text-color);
  text-decoration: none;
  margin-bottom: var(--spacing-md);
  transition: background-color var(--transition-speed) ease;
  border-left: 4px solid var(--primary-color);
  
  &:hover {
    background-color: var(--background-dark);
    text-decoration: none;
  }
  
  .icon {
    font-size: 1.5rem;
  }
  
  .details {
    flex: 1;
  }
  
  .title {
    font-weight: 600;
    margin-bottom: 4px;
  }
  
  .description {
    font-size: 0.875rem;
    color: var(--text-light);
  }
  
  .source {
    font-size: 0.75rem;
    color: var(--primary-color);
    margin-top: 4px;
  }
`;

const PoliciesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: var(--spacing-xl);
  
  th {
    text-align: left;
    padding: var(--spacing-md);
    background-color: var(--primary-color);
    color: white;
  }
  
  td {
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--background-light);
  }
  
  tr:nth-child(even) {
    background-color: var(--background-light);
  }
`;

const ReferencePage = () => {
  return (
    <>
      <PageTitle>Reference Guide</PageTitle>
      
      <SectionTitle>Quick Reference Guides</SectionTitle>
      <p>Access these guides for quick reference on PHI handling procedures. All guides can be printed if needed.</p>
      
      <div>
        <DocumentDownload href="/resources/email-encryption-guide.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">📄</div>
          <div className="details">
            <div className="title">Email Encryption Quick Guide</div>
            <div className="description">Step-by-step guide for encrypting emails with PHI</div>
          </div>
        </DocumentDownload>
        
        <DocumentDownload href="/resources/phi-identification-checklist.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">📄</div>
          <div className="details">
            <div className="title">PHI Identification Checklist</div>
            <div className="description">Guide to identifying what qualifies as PHI</div>
          </div>
        </DocumentDownload>
        
        <DocumentDownload href="/resources/secure-portal-instructions.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">📄</div>
          <div className="details">
            <div className="title">Secure Portal Instructions</div>
            <div className="description">Complete guide for sending and receiving encrypted emails with PHI and confidential information</div>
          </div>
        </DocumentDownload>
      </div>
      
      <section id="policies">
        <SectionTitle>Company Policies & Procedures</SectionTitle>
        <p>Access company policies and procedures related to information handling, security, and compliance.</p>
        <PoliciesTable>
          <thead>
            <tr>
              <th>Title</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Information Classification Handling Policy</td>
              <td><Button variant="tertiary" size="small" onClick={() => window.open('/resources/Aranscia Information Classification Handling Policy 1.0.pdf', '_blank')}>View</Button></td>
            </tr>
            <tr>
              <td>Privacy and Security Incident Response Policy</td>
              <td><Button variant="tertiary" size="small" onClick={() => window.open('/resources/Aranscia Privacy and Security Incident Response Policy 1.0.pdf', '_blank')}>View</Button></td>
            </tr>
            <tr>
              <td>PHI Use and Deidentification Policy</td>
              <td><Button variant="tertiary" size="small" onClick={() => window.open('/resources/Aranscia PHI Use and Deidentification Policy 1.0.pdf', '_blank')}>View</Button></td>
            </tr>
            <tr>
              <td>HIPAA Privacy Policy</td>
              <td><Button variant="tertiary" size="small" onClick={() => window.open('/resources/Aranscia HIPAA Privacy Policy 1.0.pdf', '_blank')}>View</Button></td>
            </tr>
          </tbody>
        </PoliciesTable>
      </section>
      
      <SectionTitle>Frequently Asked Questions</SectionTitle>
      <ResourcesGrid>
        <Card title="What information is considered PHI?">
          <p>
            Protected Health Information (PHI) includes any individually identifiable health information, 
            such as names, addresses, dates, phone/fax numbers, email addresses, social security numbers, 
            medical record numbers, health plan beneficiary numbers, account numbers, certificate/license numbers, 
            vehicle identifiers, device identifiers, web URLs, IP addresses, biometric identifiers, 
            full-face photographs, and any other unique identifying numbers or codes.
          </p>
        </Card>
        
        <Card title="When should I encrypt an email?">
          <p>
            You should encrypt an email anytime it contains PHI. This includes patient names 
            connected to health information, medical record numbers, treatment information, 
            billing information related to healthcare, and any other identifiable health information.
          </p>
        </Card>
        
        <Card title="What to do if PHI is sent insecurely">
          <p>
            If you discover PHI has been sent insecurely, you MUST immediately report 
            the incident to your supervisor and Security or Compliance Officer. There is no need to try to recall 
            the email.
          </p>
        </Card>
        
        <Card title="How do I know if my email was successfully encrypted?">
          <p>
            After sending an encrypted email, you can verify encryption by checking your 
            sent items folder. Properly encrypted emails will display an encryption icon 
            or indicator. Recipients will receive a notification that they've received an 
            encrypted message requiring secure access.
          </p>
        </Card>
      </ResourcesGrid>
      
      <SectionTitle>Official Regulatory Resources</SectionTitle>
      <p>Access official government and regulatory resources related to HIPAA, PHI, and privacy regulations.</p>
      
      <div>
        <ExternalResource href="https://www.hhs.gov/hipaa/for-professionals/privacy/laws-regulations/index.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">HIPAA Privacy Rule</div>
            <div className="description">Official HHS guidance on HIPAA Privacy Rule regulations and standards</div>
            <div className="source">U.S. Department of Health & Human Services</div>
          </div>
        </ExternalResource>
        
        <ExternalResource href="https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">HIPAA Security Rule</div>
            <div className="description">Standards for protecting electronic PHI including administrative, physical, and technical safeguards</div>
            <div className="source">U.S. Department of Health & Human Services</div>
          </div>
        </ExternalResource>
        
        <ExternalResource href="https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">Breach Notification Rule</div>
            <div className="description">Requirements for providing notification following a breach of unsecured PHI</div>
            <div className="source">U.S. Department of Health & Human Services</div>
          </div>
        </ExternalResource>
        
        <ExternalResource href="https://iapp.org/resources/article/us-state-privacy-legislation-tracker/#state-privacy-law-map" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">U.S. State Privacy Legislation Tracker</div>
            <div className="description">Interactive tool tracking comprehensive state privacy laws and their requirements</div>
            <div className="source">International Association of Privacy Professionals (IAPP)</div>
          </div>
        </ExternalResource>
        
        <ExternalResource href="https://www.cdc.gov/phlp/php/resources/health-insurance-portability-and-accountability-act-of-1996-hipaa.html?CDC_AAref_Val=https://www.cdc.gov/phlp/publications/topic/hipaa.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">CDC HIPAA Privacy Rule Resources</div>
            <div className="description">CDC guidance on the HIPAA Privacy Rule and public health</div>
            <div className="source">Centers for Disease Control and Prevention</div>
          </div>
        </ExternalResource>
        
        <ExternalResource href="https://www.hhs.gov/hipaa/for-professionals/special-topics/health-information-technology/index.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">Health Information Technology</div>
            <div className="description">Guidance on HIPAA and electronic health records, mobile health apps, and cloud computing</div>
            <div className="source">U.S. Department of Health & Human Services</div>
          </div>
        </ExternalResource>
        
        <ExternalResource href="https://www.hhs.gov/hipaa/for-professionals/special-topics/de-identification/index.html" target="_blank" rel="noopener noreferrer">
          <div className="icon">🔗</div>
          <div className="details">
            <div className="title">HIPAA De-identification Guidance</div>
            <div className="description">Methods and guidance for de-identification of protected health information according to the HIPAA Privacy Rule</div>
            <div className="source">U.S. Department of Health & Human Services</div>
          </div>
        </ExternalResource>
      </div>
    </>
  );
};

export default ReferencePage; 